import { startTransition, useCallback, useEffect, useRef, useState, type RefObject } from "react";
import type { Editor } from "@tiptap/react";
import type { Book, Chapter } from "../types";
import type { ManuscriptEditorHandle } from "../components/workbench/ManuscriptEditorPane";
import { chapterContentBodyChanged } from "../utils/manuscriptEditorHelpers";
import { serializeChapterContent } from "../../shared/chapterContent";

type EnsureBookShape = (book: Book) => Book;

type UseManuscriptEditingOptions = {
  bookRef: RefObject<Book | null>;
  filePathRef: RefObject<string | null>;
  activeChapterIdRef: RefObject<string>;
  activeTabRef: RefObject<string>;
  dirtyRef: RefObject<boolean>;
  ensureBookShape: EnsureBookShape;
  nowIso: () => string;
  setBook: (book: Book) => void;
  setDirty: (dirty: boolean) => void;
  /** Fired when manuscript body content is written into the book (not comment-anchor-only flushes). */
  onContentDraftPersisted?: () => void;
  /**
   * Fired on every book mutation, including the ref-only ones that never
   * re-render. The per-chapter outbox subscribes here so no edit can reach
   * `bookRef` without also being queued for the server.
   */
  onBookPersisted?: (book: Book) => void;
};

export function useManuscriptEditing({
  bookRef,
  filePathRef,
  activeChapterIdRef,
  activeTabRef,
  dirtyRef,
  ensureBookShape,
  nowIso,
  setBook,
  setDirty,
  onContentDraftPersisted,
  onBookPersisted,
}: UseManuscriptEditingOptions) {
  const manuscriptEditorRef = useRef<ManuscriptEditorHandle | null>(null);
  const [manuscriptReloadToken, setManuscriptReloadToken] = useState(0);
  const recoverySaveTimerRef = useRef<number | null>(null);
  const bookUiTimerRef = useRef<number | null>(null);
  const RECOVERY_SAVE_DEBOUNCE_MS = 1500;
  /** Keep React book state off the hot typing path; bookRef is authoritative for saves. */
  const BOOK_UI_DEBOUNCE_MS = 2500;

  const bumpManuscriptReload = useCallback(() => {
    setManuscriptReloadToken((token) => token + 1);
  }, []);

  const getManuscriptEditor = useCallback((): Editor | null => {
    return manuscriptEditorRef.current?.getEditor() ?? null;
  }, []);

  const undoManuscript = useCallback((): boolean => {
    return manuscriptEditorRef.current?.undo() ?? false;
  }, []);

  const redoManuscript = useCallback((): boolean => {
    return manuscriptEditorRef.current?.redo() ?? false;
  }, []);

  const flushManuscriptDraft = useCallback((): void => {
    manuscriptEditorRef.current?.flushDraft();
  }, []);

  const scheduleRecoverySave = useCallback((): void => {
    if (recoverySaveTimerRef.current) {
      window.clearTimeout(recoverySaveTimerRef.current);
    }
    recoverySaveTimerRef.current = window.setTimeout(() => {
      recoverySaveTimerRef.current = null;
      const current = bookRef.current;
      if (!current) return;
      void window.desktopV2Api
        .saveRecovery({
          book: current,
          filePath: filePathRef.current,
          activeChapterId: activeChapterIdRef.current,
          activeTab: activeTabRef.current,
          savedAt: nowIso(),
        })
        .catch(() => undefined);
    }, RECOVERY_SAVE_DEBOUNCE_MS);
  }, [bookRef, filePathRef, activeChapterIdRef, activeTabRef, nowIso]);

  const flushRecoverySave = useCallback((): void => {
    if (recoverySaveTimerRef.current) {
      window.clearTimeout(recoverySaveTimerRef.current);
      recoverySaveTimerRef.current = null;
    }
    const current = bookRef.current;
    if (!current) return;
    void window.desktopV2Api
      .saveRecovery({
        book: current,
        filePath: filePathRef.current,
        activeChapterId: activeChapterIdRef.current,
        activeTab: activeTabRef.current,
        savedAt: nowIso(),
      })
      .catch(() => undefined);
  }, [bookRef, filePathRef, activeChapterIdRef, activeTabRef, nowIso]);

  const flushBookUi = useCallback((): void => {
    if (bookUiTimerRef.current) {
      window.clearTimeout(bookUiTimerRef.current);
      bookUiTimerRef.current = null;
    }
    const current = bookRef.current;
    if (!current) return;
    startTransition(() => {
      if (dirtyRef.current) setDirty(true);
      setBook(current);
    });
  }, [bookRef, dirtyRef, setBook, setDirty]);

  const scheduleBookUiUpdate = useCallback((): void => {
    if (bookUiTimerRef.current) return;
    bookUiTimerRef.current = window.setTimeout(() => {
      bookUiTimerRef.current = null;
      flushBookUi();
    }, BOOK_UI_DEBOUNCE_MS);
  }, [flushBookUi]);

  const handleManuscriptTyping = useCallback((): void => {
    // Mark dirty for save/sync guards only — do not re-render App on every keystroke.
    dirtyRef.current = true;
  }, [dirtyRef]);

  const persistBookUpdate = useCallback(
    (
      next: Book,
      recovery: "debounced" | "immediate" | "skip" = "debounced"
    ): void => {
      bookRef.current = next;
      onBookPersisted?.(next);
      if (recovery === "immediate") {
        void window.desktopV2Api
          .saveRecovery({
            book: next,
            filePath: filePathRef.current,
            activeChapterId: activeChapterIdRef.current,
            activeTab: activeTabRef.current,
            savedAt: nowIso(),
          })
          .catch(() => undefined);
      } else if (recovery === "debounced") {
        scheduleRecoverySave();
      }
    },
    [
      bookRef,
      filePathRef,
      activeChapterIdRef,
      activeTabRef,
      nowIso,
      onBookPersisted,
      scheduleRecoverySave,
    ]
  );

  const handleManuscriptDraftFlush = useCallback(
    (payload: { chapterId: string; content: string; comments: Chapter["comments"] }) => {
      const current = bookRef.current;
      if (!current) return;
      const chapter = current.chapters.find((ch) => ch.id === payload.chapterId);
      if (!chapter) return;
      const contentChanged = chapterContentBodyChanged(chapter.content, payload.content);
      const commentsChanged = payload.comments !== chapter.comments;
      if (!contentChanged && !commentsChanged) return;

      const touchedAt = nowIso();
      const next = ensureBookShape({
        ...current,
        updatedAt: touchedAt,
        chapters: current.chapters.map((ch) =>
          ch.id === payload.chapterId
            ? {
                ...ch,
                content: contentChanged ? serializeChapterContent(payload.content) : ch.content,
                comments: payload.comments,
                updatedAt: touchedAt,
              }
            : ch
        ),
      });
      dirtyRef.current = true;
      persistBookUpdate(next, "immediate");
      scheduleBookUiUpdate();
      if (contentChanged) onContentDraftPersisted?.();
    },
    [
      bookRef,
      dirtyRef,
      ensureBookShape,
      nowIso,
      onContentDraftPersisted,
      persistBookUpdate,
      scheduleBookUiUpdate,
    ]
  );

  const markBookDirty = useCallback((): void => {
    dirtyRef.current = true;
    setDirty(true);
  }, [dirtyRef, setDirty]);

  useEffect(() => {
    return () => {
      if (recoverySaveTimerRef.current) window.clearTimeout(recoverySaveTimerRef.current);
      if (bookUiTimerRef.current) window.clearTimeout(bookUiTimerRef.current);
    };
  }, []);

  return {
    manuscriptEditorRef,
    manuscriptReloadToken,
    bumpManuscriptReload,
    getManuscriptEditor,
    undoManuscript,
    redoManuscript,
    flushManuscriptDraft,
    flushRecoverySave,
    flushBookUi,
    scheduleRecoverySave,
    persistBookUpdate,
    markBookDirty,
    handleManuscriptTyping,
    handleManuscriptDraftFlush,
  };
}
